<template>
  <div class="user-management">
    <div class="header">
      <h2>{{ $t('user.title') }}</h2>
      <div class="actions">
        <el-button type="info" size="small" @click="showLogDialog">
          <el-icon><Document /></el-icon>
          {{ $t('user.viewLogs') }}
        </el-button>
        <el-button type="primary" size="small" @click="addUserDialogVisible = true" v-show="isAdmin">
          <el-icon><Plus /></el-icon>
          {{ $t('user.addUser') }}
        </el-button>
      </div>
    </div>

    <!-- 新增用户对话框 -->
    <el-dialog
      v-model="addUserDialogVisible"
      :title="$t('user.addUser')"
      :width="isMobile ? '100%' : '500px'"
      @closed="resetAddUserForm"
    >
      <el-form
        ref="addUserFormRef"
        :model="addUserForm"
        :rules="addUserRules"
        label-width="100px"
      >
        <el-form-item :label="$t('user.username')" prop="username">
          <el-input v-model="addUserForm.username" :placeholder="$t('user.usernameRequired')" />
        </el-form-item>
        <el-form-item :label="$t('user.password')" prop="password">
          <el-input
            v-model="addUserForm.password"
            type="password"
            :placeholder="$t('user.passwordRequired')"
            show-password
          />
        </el-form-item>
        <el-form-item :label="$t('user.confirmPassword')" prop="confirmPassword">
          <el-input
            v-model="addUserForm.confirmPassword"
            type="password"
            :placeholder="$t('user.passwordMismatch')"
            show-password
          />
        </el-form-item>
        <el-form-item :label="$t('user.role')" prop="role">
          <el-select v-model="addUserForm.role" :placeholder="$t('user.roleRequired')">
            <el-option :label="$t('user.normalUser')" value="user" />
            <el-option :label="$t('user.admin')" value="admin" />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('user.nickname')">
          <el-input v-model="addUserForm.nickname" :placeholder="$t('common.optional')" />
        </el-form-item>
        <el-form-item :label="$t('user.email')">
          <el-input v-model="addUserForm.email" :placeholder="$t('common.optional')" />
        </el-form-item>
        <el-form-item :label="$t('user.telephone')">
          <el-input v-model="addUserForm.telephone" :placeholder="$t('common.optional')" />
        </el-form-item>
        <el-form-item :label="$t('user.remark')">
          <el-input
            v-model="addUserForm.remark"
            type="textarea"
            :rows="2"
            :placeholder="$t('common.optional')"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="addUserDialogVisible = false">{{ $t('common.cancel') }}</el-button>
          <el-button
            type="primary"
            @click="submitAddUser"
            :loading="addUserLoading"
          >
            {{ $t('common.confirm') }}
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 新增：修改超级管理员用户名对话框 -->
    <el-dialog
      v-model="renameSuperDialogVisible"
      :title="$t('user.modifySuperAdmin')"
      :width="isMobile ? '100%' : '500px'"
      @closed="resetRenameForm"
    >
      <el-form
        ref="renameFormRef"
        :model="renameForm"
        :rules="renameRules"
        label-width="120px"
      >
        <el-form-item :label="$t('user.currentUsername')">
          <el-input v-model="renameForm.currentUsername" disabled />
        </el-form-item>
        <el-form-item :label="$t('user.newUsername')" prop="newUsername">
          <el-input
            v-model="renameForm.newUsername"
            :placeholder="$t('user.usernameLength')"
            clearable
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="renameSuperDialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button
          type="primary"
          @click="submitRenameSuperUser"
          :loading="renaming"
        >
          {{ $t('common.confirm') }}
        </el-button>
      </template>
    </el-dialog>

     <!-- 编辑用户对话框 -->
    <el-dialog
      v-model="editUserDialogVisible"
      :title="$t('user.editUser')"
      :width="isMobile ? '100%' : '500px'"
      @closed="resetEditUserForm"
    >
      <el-form
        ref="editUserFormRef"
        :model="editUserForm"
        :rules="editUserRules"
        label-width="100px"
      >
        <el-form-item :label="$t('user.username')">
          <el-input v-model="editUserForm.username" disabled />
        </el-form-item>
        <el-form-item :label="$t('user.password')" prop="password">
          <el-input
            v-model="editUserForm.password"
            type="password"
            :placeholder="$t('user.passwordEmptyHint')"
            show-password
          />
        </el-form-item>
        <el-form-item :label="$t('user.confirmPassword')" prop="confirmPassword">
          <el-input
            v-model="editUserForm.confirmPassword"
            type="password"
            :placeholder="$t('user.passwordMismatch')"
            show-password
          />
        </el-form-item>
        <el-form-item :label="$t('user.role')" prop="role">
          <el-select v-model="editUserForm.role" :placeholder="$t('user.roleRequired')" :disabled="editUserForm.isSuperAdmin || !isAdmin">
            <el-option :label="$t('user.normalUser')" value="user" />
            <el-option :label="$t('user.admin')" value="admin" />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('user.nickname')">
          <el-input v-model="editUserForm.nickname" :placeholder="$t('common.optional')" />
        </el-form-item>
        <el-form-item :label="$t('user.email')">
          <el-input v-model="editUserForm.email" :placeholder="$t('common.optional')" />
        </el-form-item>
        <el-form-item :label="$t('user.telephone')">
          <el-input v-model="editUserForm.telephone" :placeholder="$t('common.optional')" />
        </el-form-item>
        <el-form-item :label="$t('user.remark')">
          <el-input
            v-model="editUserForm.remark"
            type="textarea"
            :rows="2"
            :placeholder="$t('common.optional')"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="editUserDialogVisible = false">{{ $t('common.cancel') }}</el-button>
          <el-button
            type="primary"
            @click="submitEditUser"
            :loading="editUserLoading"
          >
            {{ $t('common.confirm') }}
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 用户管理日志查看对话框 -->
    <el-dialog
      v-model="logDialogVisible"
      :title="$t('user.title') + ' ' + $t('log.title')"
      :width="isMobile ? '95%' : '900px'"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      class="settings-dialog log-dialog"
    >
      <div class="log-toolbar">
        <div class="log-stats">
          <span>{{ $t('log.logTotal', { count: logTotal }) }}</span>
          <div class="auto-refresh-control">
            <span class="refresh-label">{{ $t('log.autoRefresh') }}</span>
            <el-switch
              v-model="autoRefreshEnabled"
              :active-value="true"
              :inactive-value="false"
              @change="toggleAutoRefresh"
              size="small"
              active-color="#409eff"
            />
            <span class="refresh-interval" v-if="autoRefreshEnabled">(5{{ $t('log.second') }})</span>
          </div>
        </div>
        <div class="log-actions">
          <el-button
            type="success"
            size="small"
            @click="downloadAllLogs"
            :loading="logDownloading"
            :icon="Download"
          >
            {{ $t('log.downloadAllLogs') }}
          </el-button>
          <el-button
            type="primary"
            size="small"
            @click="refreshLogs"
            :loading="logLoading"
            :icon="Refresh"
          >
            {{ $t('common.refresh') }}
          </el-button>
          <el-button
            type="danger"
            size="small"
            @click="clearLogs"
            :loading="logClearing"
          >
            {{ $t('log.clearLogs') }}
          </el-button>
        </div>
      </div>

      <div class="log-content" ref="logContentRef">
        <div v-if="logsData.length === 0" class="empty-log">
          <el-icon><InfoFilled /></el-icon>
          <p>{{ $t('log.noLogs') }}</p>
        </div>
        <div v-else class="log-list">
          <div
            v-for="(log, index) in logsData"
            :key="index"
            class="log-item"
            :class="`log-level-${log.Level.toLowerCase()}`"
          >
            <span class="log-time">{{ log.Time }}</span>
            <span class="log-level" :class="`level-${log.Level.toLowerCase()}`">
              [{{ log.Level }}]
            </span>
            <span class="log-message">{{ log.Content }}</span>
          </div>
        </div>
      </div>

      <div class="log-pagination" v-if="logTotal > 0">
        <el-pagination
          v-model:current-page="logCurrentPage"
          v-model:page-size="logPageSize"
          :page-sizes="[20, 50, 100, 200]"
          :total="logTotal"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleLogSizeChange"
          @current-change="handleLogPageChange"
          small
        />
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="logDialogVisible = false">{{ $t('common.close') }}</el-button>
        </div>
      </template>
    </el-dialog>

    <div class="table-container">
      <table class="user-table">
        <thead>
          <tr>
            <th>{{ $t('user.username') }}</th>
            <th>{{ $t('user.role') }}</th>
            <th>{{ $t('user.nickname') }}</th>
            <th>{{ $t('user.email') }}</th>
            <th>{{ $t('user.telephone') }}</th>
            <th>{{ $t('user.roleStatus') }}</th>
            <th>{{ $t('user.loginTime') }}</th>
            <th>{{ $t('user.createTime') }}</th>
            <th>{{ $t('user.remark') }}</th>
            <th>{{ $t('common.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in users" :key="user.Username">
            <td data-label="username">{{ user.Username }}</td>
            <td data-label="role">
                <span class="role-tag" :class="{
                  'super-admin': user.IsSuperAdmin,
                  'admin': !user.IsSuperAdmin && user.IsAdmin,
                  'user': !user.IsAdmin
                }">
                  {{ user.Role || $t('user.normalUser') }}
                </span>
            </td>
            <td data-label="nickname">{{ user.Nickname || '-' }}</td>
            <td data-label="email">{{ user.Email || '-' }}</td>
            <td data-label="telephone">{{ user.Telephone || '-' }}</td>
            <td data-label="status">
              <span class="status-badge" :class="{'active': user.RoleStatus === $t('user.active'), 'inactive': user.RoleStatus !== $t('user.active')}">
                {{ user.RoleStatus || $t('user.active') }}
              </span>
            </td>
            <td data-label="loginTime">{{ formatDateTime(user.LoginTime) }}</td>
            <td data-label="createTime">{{ formatDateTime(user.CreateTime) }}</td>
            <td data-label="remark" class="note-cell">{{ user.Remark || '-' }}</td>
            <td class="action-cell">
              <button v-if="!user.IsSuperAdmin && isAdmin" class="btn-delete" @click="deleteUser(user.Username)">{{ $t('common.delete') }}</button>
              <button v-if="user.IsSuperAdmin" class="btn-edit" @click="changeSuperName(user.Username)">{{ $t('user.changeUsername') }}</button>
              <button class="btn-edit" @click="openEditDialog(user)">{{ $t('common.edit') }}</button>
              <button
                v-if="!user.IsSuperAdmin && isAdmin"
                :class="(user.RoleStatus === $t('user.active') || !user.RoleStatus) ? 'btn-deactivate' : 'btn-activate'"
                @click="toggleUserStatus(user)"
              >
                {{ (user.RoleStatus === $t('user.active') || !user.RoleStatus) ? $t('user.deactivate') : $t('user.activate') }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL } from '@/config';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Document, Plus, InfoFilled } from '@element-plus/icons-vue';

export default {
  components: {
    Document,
    Plus,
    InfoFilled
  },
  data() {
    const isMobile = window.innerWidth < 768;

      // 验证确认密码是否与密码一致
    const validateConfirmPassword = (rule, value, callback) => {
      if (value !== this.addUserForm.password) {
        callback(new Error(this.$t('user.passwordMismatch')));
      } else {
        callback();
      }
    };

    // 验证确认密码是否与密码一致（编辑时可为空）
    const validateEditConfirmPassword = (rule, value, callback) => {
      if (this.editUserForm.password && value !== this.editUserForm.password) {
        callback(new Error(this.$t('user.passwordMismatch')));
      } else {
        callback();
      }
    };

    // 新用户名验证规则
    const validateNewUsername = (rule, value, callback) => {
      if (!value) {
        return callback(new Error(this.$t('user.usernameRequired')));
      }
      if (value === this.renameForm.currentUsername) {
        return callback(new Error(this.$t('user.usernameSameAsCurrent')));
      }
      if (value.length < 3 || value.length > 20) {
        return callback(new Error(this.$t('user.usernameLength')));
      }
      callback();
    };

    return {
      // 新增用户对话框相关数据
      isMobile,
      addUserDialogVisible: false,
      addUserLoading: false,
      addUserForm: {
        username: '',
        password: '',
        confirmPassword: '',
        role: 'user',
        nickname: '',
        email: '',
        telephone: '',
        remark: ''
      },
      addUserRules: {
        username: [
          { required: true, message: this.$t('user.usernameRequired'), trigger: 'blur' },
          { min: 3, max: 20, message: this.$t('user.usernameLength'), trigger: 'blur' }
        ],
        password: [
          { required: true, message: this.$t('user.passwordRequired'), trigger: 'blur' },
          { min: 6, max: 20, message: this.$t('user.passwordMinLength'), trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: this.$t('user.passwordRequired'), trigger: 'blur' },
          { validator: validateConfirmPassword, trigger: 'blur' }
        ],
        role: [
          { required: true, message: this.$t('user.roleRequired'), trigger: 'change' }
        ]
      },

      // 编辑用户对话框相关数据
      editUserDialogVisible: false,
      editUserLoading: false,
      editUserForm: {
        username: '',
        password: '',
        confirmPassword: '',
        role: 'user',
        nickname: '',
        email: '',
        telephone: '',
        remark: '',
        isSuperAdmin: false
      },
      editUserRules: {
        password: [
          { min: 6, max: 20, message: this.$t('user.passwordMinLength'), trigger: 'blur' }
        ],
        confirmPassword: [
          { validator: validateEditConfirmPassword, trigger: 'blur' }
        ],
        role: [
          { required: true, message: this.$t('user.roleRequired'), trigger: 'change' }
        ]
      },
      // 新增：超级管理员重命名相关数据
      renameSuperDialogVisible: false,
      renaming: false,
      renameForm: {
        currentUsername: '',
        newUsername: ''
      },
      renameRules: {
        newUsername: [
          { validator: validateNewUsername, trigger: 'blur' }
        ]
      },
      // 日志相关数据
      logDialogVisible: false,
      logLoading: false,
      logClearing: false,
      logDownloading: false,
      logsData: [],
      logTotal: 0,
      logCurrentPage: 1,
      logPageSize: 50,
      autoRefreshEnabled: true,
      logRefreshInterval: null,
      isAdmin: false,
      users: [],
      loading: false,
      refreshInterval: null
    }
  },
  created() {
    this.fetchUserData();
    this.setupAutoRefresh();
  },
  beforeUnmount() {
    this.clearAutoRefresh();
    this.stopLogAutoRefresh();
  },
  methods: {
    // 设置自动刷新
    setupAutoRefresh() {
      // 每10秒自动刷新一次数据
      this.refreshInterval = setInterval(this.fetchUserData, 10000);
    },
    
    // 清除自动刷新
    clearAutoRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
    },
    
    async fetchUserData() {
      this.loading = true;
      try {
        const response = await fetch(`${API_BASE_URL}/list-userInfo`, {
          credentials: 'include' // 确保携带cookie
        });

        const data = await response.json();

        if (data.errCode === 1000) {
          // 未登录状态，跳转到登录页
          ElMessage.warning(this.$t('common.notLoggedIn'));
          this.$router.push('/user-login?callback=user-mgmt');
          return;
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || this.$t('user.fetchFailed'));
        }

        this.isAdmin = data.isAdmin; // 当前用户角色

        // 处理API返回的用户数据
        this.users = data.user.map(user => ({
          ...user,
          RoleStatus: user.RoleStatus || this.$t('user.active') // 默认状态为激活
        }));

      } catch (error) {
        console.error(this.$t('user.fetchFailed'), error);
        ElMessage.error(error.message || this.$t('user.fetchFailed'));
      } finally {
        this.loading = false;
      }
    },
    
    // 显示日志对话框
    showLogDialog() {
      this.logDialogVisible = true;
      this.fetchLogs();
      // 开启日志自动刷新（如果开启状态）
      if (this.autoRefreshEnabled) {
        this.startLogAutoRefresh();
      }
    },

    // 切换自动刷新
    toggleAutoRefresh(enabled) {
      if (enabled) {
        this.startLogAutoRefresh();
        ElMessage.success(this.$t('log.autoRefreshEnabled'));
      } else {
        this.stopLogAutoRefresh();
        ElMessage.info(this.$t('log.autoRefreshDisabled'));
      }
    },

    // 启动日志自动刷新
    startLogAutoRefresh() {
      this.stopLogAutoRefresh(); // 先停止已有的定时器
      this.logRefreshInterval = setInterval(() => {
        if (this.logDialogVisible) {
          this.fetchLogs(false); // 静默刷新，不显示加载状态
        }
      }, 5000);
    },

    // 停止日志自动刷新
    stopLogAutoRefresh() {
      if (this.logRefreshInterval) {
        clearInterval(this.logRefreshInterval);
        this.logRefreshInterval = null;
      }
    },

    // 获取日志
    async fetchLogs(showLoading = true) {
      try {
        if (showLoading) {
          this.logLoading = true;
        }

        const params = new URLSearchParams({
          module: 'user',
          page: this.logCurrentPage,
          size: this.logPageSize
        });

        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        });

        if (!response.ok) throw new Error(this.$t('common.networkError'));

        const data = await response.json();
        if (data.errCode === 1000) {
          ElMessage.error(this.$t('common.notLoggedIn'));
          this.$router.push('/user-login?callback=user-mgmt');
          return;
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || this.$t('log.fetchFailed'));
        }

        this.logsData = data.data || [];
        this.logTotal = data.count || 0;

        // 只有在非自动刷新（即手动操作）时才滚动到顶部
        if (showLoading && this.$refs.logContentRef) {
          this.$refs.logContentRef.scrollTop = 0;
        }
      } catch (error) {
        console.error(this.$t('log.fetchFailed'), error);
        if (showLoading) {
          ElMessage.error(this.$t('log.fetchFailed') + ': ' + (error.message || this.$t('common.error')));
        }
      } finally {
        if (showLoading) {
          this.logLoading = false;
        }
      }
    },

    // 刷新日志
    refreshLogs() {
      this.fetchLogs(true);
    },

    // 清空日志
    async clearLogs() {
      try {
        await ElMessageBox.confirm(this.$t('log.confirmClear'), this.$t('common.warning'), {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        });

        this.logClearing = true;

        const params = new URLSearchParams({
          module: 'user'
        });

        const response = await fetch(`${API_BASE_URL}/clear-moduleLog?${params.toString()}`, {
          method: 'DELETE',
          credentials: 'include'
        });

        if (!response.ok) throw new Error(this.$t('common.networkError'));

        const data = await response.json();
        if (data.errCode === 1000) {
          ElMessage.error(this.$t('common.notLoggedIn'));
          this.$router.push('/user-login?callback=user-mgmt');
          return;
        }
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || this.$t('log.clearFailed'));
        }

        ElMessage.success(this.$t('log.cleared'));
        this.fetchLogs(true);
      } catch (error) {
        if (error !== 'cancel') {
          console.error(this.$t('log.clearFailed'), error);
          ElMessage.error(this.$t('log.clearFailed') + ': ' + (error.message || this.$t('common.error')));
        }
      } finally {
        this.logClearing = false;
      }
    },

    // 下载所有日志
    async downloadAllLogs() {
      try {
        this.logDownloading = true;
        
        // 获取所有日志（设置一个足够大的size值）
        const params = new URLSearchParams({
          module: 'user',
          page: 1,
          size: 10000 // 设置一个较大的数值，获取尽可能多的日志
        });
        
        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        });
        
        if (!response.ok) throw new Error(this.$t('common.networkError'));

        const data = await response.json();
        if (data.errCode === 1000) {
          ElMessage.error(this.$t('common.notLoggedIn'));
          this.$router.push('/user-login?callback=user-mgmt');
          return;
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || this.$t('log.fetchFailed'));
        }

        const allLogs = data.data || [];

        if (allLogs.length === 0) {
          ElMessage.warning(this.$t('log.noLogsToDownload'));
          return;
        }
        
        // 格式化所有日志内容
        let logContent = '';
        allLogs.forEach(log => {
          logContent += `[${log.Time}] [${log.Level}] ${log.Content}\n`;
        });
        
        // 创建下载链接
        const blob = new Blob([logContent], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `user_management_logs_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.log`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        ElMessage.success(this.$t('log.logDownloadSuccess', { count: allLogs.length }));
      } catch (error) {
        console.error(this.$t('log.downloadFailed'), error);
        ElMessage.error(this.$t('log.downloadFailed') + ': ' + (error.message || this.$t('common.error')));
      } finally {
        this.logDownloading = false;
      }
    },

    // 处理每页条数变化
    handleLogSizeChange(size) {
      this.logPageSize = size;
      this.logCurrentPage = 1;
      this.fetchLogs(true);
    },

    // 处理页码变化
    handleLogPageChange(page) {
      this.logCurrentPage = page;
      this.fetchLogs(true);
    },
    
    formatDateTime(datetime) {
      if (!datetime || datetime === '1970-01-01 08:00') return '-';
      return datetime.replace(' ', '  ');
    },
    formatDate(date) {
      if (!date || date === '1970-01-01 08:00') return '-';
      return date.split(' ')[0];
    },
    // 提交新增用户表单
    async submitAddUser() {
      try {
        this.addUserLoading = true;
        
        // 验证表单
        await this.$refs.addUserFormRef.validate();
        
        // 准备表单数据
        const formData = new URLSearchParams();
        formData.append('username', this.addUserForm.username);

        const simpleEncrypt = (str) => {
          const key = 0x5A;
          return Array.from(str).map(c => String.fromCharCode(c.charCodeAt(0) ^ key)).join('');
        };
        formData.append('password', simpleEncrypt(this.addUserForm.password));

        if (this.addUserForm.role == 'admin') {
          formData.append('isAdmin', 'yes');
        }
        formData.append('nickname', this.addUserForm.nickname);
        formData.append('email', this.addUserForm.email);
        formData.append('telephone', this.addUserForm.telephone);
        formData.append('remark', this.addUserForm.remark);
        
        // 调用API添加用户
        const response = await fetch(`${API_BASE_URL}/add-userInfo`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formData.toString(),
          credentials: 'include'
        });
        
        const result = await response.json();
        
        if (result.errCode !== 0) {
          throw new Error(result.errMsg || this.$t('common.operationFailed'));
        }

        ElMessage.success(this.$t('user.addSuccess'));
        this.addUserDialogVisible = false;
        this.fetchUserData(); // 刷新用户列表
      } catch (error) {
        console.error(this.$t('common.operationFailed'), error);
        ElMessage.error(error.message || this.$t('common.operationFailed'));
      } finally {
        this.addUserLoading = false;
      }
    },
    // 重置新增用户表单
    resetAddUserForm() {
      this.$refs.addUserFormRef?.resetFields();
    },
    // 打开编辑对话框
    openEditDialog(user) {
      this.editUserForm = {
        username: user.Username,
        password: '',
        confirmPassword: '',
        role: user.IsAdmin ? 'admin' : 'user',
        nickname: user.Nickname || '',
        email: user.Email || '',
        telephone: user.Telephone || '',
        remark: user.Remark || '',
        isSuperAdmin: user.IsSuperAdmin
      };
      this.editUserDialogVisible = true;
    },
    // 提交编辑用户表单
    async submitEditUser() {
      try {
        this.editUserLoading = true;
        
        // 验证表单
        await this.$refs.editUserFormRef.validate();
        
        // 准备表单数据
        const formData = new URLSearchParams();
        formData.append('username', this.editUserForm.username);

        const simpleEncrypt = (str) => {
          const key = 0x5A;
          return Array.from(str).map(c => String.fromCharCode(c.charCodeAt(0) ^ key)).join('');
        };

        // 只有密码不为空时才添加密码字段
        if (this.editUserForm.password) {
          formData.append('password', simpleEncrypt(this.editUserForm.password));
        }
        
        // 超级管理员角色不可修改
        if (!this.editUserForm.isSuperAdmin) {
           if (this.editUserForm.role == 'admin') {
            formData.append('isAdmin', 'yes');
          }
        }
        
        formData.append('nickname', this.editUserForm.nickname);
        formData.append('email', this.editUserForm.email);
        formData.append('telephone', this.editUserForm.telephone);
        formData.append('remark', this.editUserForm.remark);
        
        // 调用API更新用户信息
        const response = await fetch(`${API_BASE_URL}/update-userInfo`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formData.toString(),
          credentials: 'include'
        });
        
        const result = await response.json();
        
        if (result.errCode !== 0) {
          throw new Error(result.errMsg || this.$t('common.operationFailed'));
        }

        ElMessage.success(this.$t('user.editSuccess'));
        this.editUserDialogVisible = false;
        this.fetchUserData(); // 刷新用户列表
      } catch (error) {
        console.error(this.$t('common.operationFailed'), error);
        ElMessage.error(error.message || this.$t('common.operationFailed'));
      } finally {
        this.editUserLoading = false;
      }
    },
    
    // 重置编辑用户表单
    resetEditUserForm() {
      this.$refs.editUserFormRef?.resetFields();
    },
    async toggleUserStatus(user) {
      try {
        // 请求POST接口
        const formData = new URLSearchParams();
        formData.append('username', user.Username);

        if (user.RoleStatus === this.$t('user.active')) {
          // 调用API更新用户信息
          const response = await fetch(`${API_BASE_URL}/disable-userInfo`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: formData.toString(),
            credentials: 'include'
          });

          const result = await response.json();
          if (result.errCode !== 0) {
            throw new Error(result.errMsg || this.$t('user.deactivateFailed'));
          }

          ElMessage.success(this.$t('user.userDeactivated') + `: ${user.Username}`);
          this.fetchUserData(); // 刷新用户列表
        } else {
          const response = await fetch(`${API_BASE_URL}/enable-userInfo`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: formData.toString(),
            credentials: 'include'
          });

          const result = await response.json();
          if (result.errCode !== 0) {
            throw new Error(result.errMsg || this.$t('user.activateFailed'));
          }

          ElMessage.success(this.$t('user.userActivated') + `: ${user.Username}`);
          this.fetchUserData(); // 刷新用户列表
        }
      } catch (error) {
        console.error(this.$t('user.statusUpdateFailed'), error);
        ElMessage.error(error.message || this.$t('user.statusUpdateFailed'));
      }
    },
    
    
changeSuperName(username) {
      this.renameForm.currentUsername = username;
      this.renameForm.newUsername = '';
      this.renameSuperDialogVisible = true;
    },
    
    // 新增：提交超级管理员用户名修改
    async submitRenameSuperUser() {
      try {
        await this.$refs.renameFormRef.validate();
        this.renaming = true;
        
        const formData = new URLSearchParams();
        formData.append('username', this.renameForm.newUsername);

        const response = await fetch(`${API_BASE_URL}/update-superUser`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formData,
          credentials: 'include'
        });

        const result = await response.json();
        
        if (result.errCode !== 0) {
          throw new Error(result.errMsg || this.$t('user.renameFailed'));
        }

        ElMessage.success(this.$t('user.userRenamed'));

        // 执行登出
        await fetch(`${API_BASE_URL}/logout`, {
          method: 'POST',
          credentials: 'include',
        });

        // 清除用户信息
        localStorage.removeItem('userInfo');
        this.renameSuperDialogVisible = false;

        // 跳转到登录页
        this.$router.push('/user-login?callback=user-mgmt');

      } catch (error) {
        console.error(this.$t('user.renameFailed'), error);
        ElMessage.error(error.message || this.$t('user.renameFailed'));
      } finally {
        this.renaming = false;
      }
    },
    
    // 新增：重置重命名表单
    resetRenameForm() {
      this.$refs.renameFormRef?.resetFields();
    },

    // 删除用户方法
    async deleteUser(username) {
      try {
        // 二次确认弹窗
        await ElMessageBox.confirm(
          this.$t('user.deleteConfirm').replace('{username}', username),
          this.$t('common.warning'),
          {
            confirmButtonText: this.$t('common.confirm'),
            cancelButtonText: this.$t('common.cancel'),
            type: 'warning',
            center: true,
            beforeClose: async (action, instance, done) => {
              if (action === 'confirm') {
                instance.confirmButtonLoading = true;
                try {
                  // 调用删除API
                  const response = await fetch(
                    `${API_BASE_URL}/delete-userInfo?name=${encodeURIComponent(username)}`,
                    {
                      method: 'DELETE',
                      credentials: 'include'
                    }
                  );

                  const result = await response.json();

                  if (result.errCode !== 0) {
                    throw new Error(result.errMsg || this.$t('common.operationFailed'));
                  }

                  ElMessage.success(this.$t('user.deleteSuccess'));
                  this.fetchUserData(); // 刷新用户列表
                  done();
                } catch (error) {
                  console.error(this.$t('common.operationFailed'), error);
                  ElMessage.error(error.message || this.$t('common.operationFailed'));
                  instance.confirmButtonLoading = false;
                }
              } else {
                done();
              }
            }
          }
        );
      } catch (error) {
        // 用户点击取消或关闭弹窗
        if (error !== 'cancel' && error !== 'close') {
          console.error(this.$t('common.operationFailed'), error);
        }
      }
    }
  }
}
</script>

<style scoped>
/* 原有的样式保持不变 */
.user-management {
  background-color: #ffffff;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  font-family: 'Helvetica Neue', Arial, sans-serif;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.header h2 {
  color: #303133;
  font-size: 20px;
  font-weight: 500;
  margin: 0;
}

.table-container {
  overflow-x: auto;
}

.user-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.user-table th, .user-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #ebeef5;
  text-align: left;
}

.user-table th {
  background-color: #f5f7fa;
  color: #606266;
  font-weight: 500;
}

.user-table tbody tr:hover {
  background-color: #f5f7fa;
}

/* 按钮样式 */
button {
  padding: 6px 12px;
  margin: 0 4px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.3s;
}

.btn-add {
  background-color: #67c23a;
  color: white;
}

.btn-add:hover {
  background-color: #5daf34;
}

.btn-edit {
  background-color: #409eff;
  color: white;
}

.btn-edit:hover {
  background-color: #3a8ee6;
}

.btn-delete {
  background-color: #f56c6c;
  color: white;
}

.btn-delete:hover {
  background-color: #e05e5e;
}

.btn-activate {
  background-color: #67c23a;
  color: white;
}

.btn-activate:hover {
  background-color: #5daf34;
}

.btn-deactivate {
  background-color: #e6a23c;
  color: white;
}

.btn-deactivate:hover {
  background-color: #d1922c;
}

/* 标签样式 */
.role-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  color: white;
}

.role-tag.super-admin {
  background-color: #f56c6c;
}

.role-tag.admin {
  background-color: #409eff;
}

.role-tag.user {
  background-color: #67c23a;
}

.status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
}

.status-badge.active {
  background-color: #f0f9eb;
  color: #67c23a;
  border: 1px solid #e1f3d8;
}

.status-badge.inactive {
  background-color: #fef0f0;
  color: #f56c6c;
  border: 1px solid #fde2e2;
}

.note-cell {
  color: #909399;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.action-cell {
  white-space: nowrap;
}

/* 日志对话框样式 */
.log-dialog {
  :deep(.el-dialog__body) {
    padding: 20px;
    max-height: 70vh;
    overflow-y: auto;
  }
}

.log-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
  
  .log-stats {
    display: flex;
    align-items: center;
    gap: 24px;
    font-size: 13px;
    color: #606266;
    
    .auto-refresh-control {
      display: flex;
      align-items: center;
      gap: 8px;
      padding-left: 16px;
      border-left: 1px solid #dcdfe6;
      
      .refresh-label {
        font-size: 12px;
        color: #909399;
      }
      
      .refresh-interval {
        font-size: 12px;
        color: #67c23a;
      }
    }
  }
  
  .log-actions {
    display: flex;
    gap: 8px;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
    
    .log-stats {
      width: 100%;
      flex-wrap: wrap;
      gap: 12px;
      
      .auto-refresh-control {
        padding-left: 0;
        border-left: none;
      }
    }
    
    .log-actions {
      width: 100%;
      justify-content: flex-end;
    }
  }
  
  @media (max-width: 480px) {
    .log-stats {
      font-size: 12px;
    }
  }
}

.log-content {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #fafafa;
  margin-bottom: 16px;
  max-height: 400px;
  overflow-y: auto;
  
  .empty-log {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px;
    color: #909399;
    
    .el-icon {
      font-size: 32px;
      margin-bottom: 12px;
    }
    
    p {
      margin: 0;
      font-size: 14px;
    }
  }
  
  .log-list {
    padding: 12px;
    
    .log-item {
      padding: 8px 12px;
      border-bottom: 1px solid #e4e7ed;
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      font-size: 13px;
      line-height: 1.5;
      display: flex;
      align-items: flex-start;
      gap: 8px;
      
      &:last-child {
        border-bottom: none;
      }
      
      &.log-level-error {
        background-color: #fef0f0;
      }
      
      &.log-level-warn {
        background-color: #fdf6ec;
      }
      
      .log-time {
        color: #909399;
        font-size: 12px;
        white-space: nowrap;
        flex-shrink: 0;
      }
      
      .log-level {
        font-weight: 600;
        font-size: 12px;
        white-space: nowrap;
        flex-shrink: 0;
        
        &.level-error {
          color: #f56c6c;
        }
        
        &.level-warn {
          color: #e6a23c;
        }
        
        &.level-info {
          color: #409eff;
        }
      }
      
      .log-message {
        color: #303133;
        word-break: break-all;
        flex: 1;
      }
    }
  }
  
  @media (max-width: 768px) {
    max-height: 350px;
    
    .log-list {
      padding: 8px;
      
      .log-item {
        font-size: 12px;
        flex-wrap: wrap;
        gap: 4px;
        
        .log-time {
          width: 100%;
          margin-bottom: 2px;
        }
        
        .log-message {
          width: 100%;
          margin-left: 0;
        }
      }
    }
  }
  
  @media (max-width: 480px) {
    max-height: 300px;
    
    .log-list {
      .log-item {
        font-size: 11px;
      }
    }
  }
}

.log-pagination {
  display: flex;
  justify-content: flex-end;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
}

/* Element Plus 样式覆盖 */
:deep(.el-button) {
  margin-left: 8px;
}

:deep(.el-button--small) {
  padding: 6px 12px;
  font-size: 12px;
}

:deep(.el-switch) {
  @media (max-width: 768px) {
    .el-switch__core {
      width: 36px !important;
      height: 18px !important;
      
      &:after {
        width: 14px !important;
        height: 14px !important;
      }
    }
  }
}

/* 响应式调整 */
@media (max-width: 768px) {
  .header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .actions {
    width: 100%;
    display: flex;
    justify-content: space-between;
  }
  
  button {
    padding: 8px 12px;
    flex: 1;
    margin: 0 4px;
  }
}

/* 响应式表格 - 移动端适配 */
@media (max-width: 768px) {
  .user-table {
    width: 100%;
    border: 0;
  }
  
  .user-table thead {
    display: none;
  }
  
  .user-table tr {
    display: block;
    margin-bottom: 16px;
    border: 1px solid #ebeef5;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    padding: 12px;
    background: #fff;
  }
  
  .user-table td {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    border-bottom: 1px solid #f0f0f0;
    text-align: right;
  }
  
  .user-table td:last-child {
    border-bottom: none;
  }
  
  .user-table td::before {
    content: attr(data-label);
    font-weight: 500;
    color: #909399;
    margin-right: 12px;
    text-align: left;
  }
  
  /* 操作按钮适配 */
  .action-cell {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-end;
  }
  
  .action-cell button {
    flex: 1;
    min-width: 60px;
    padding: 6px 8px;
    font-size: 12px;
  }
  
  /* 角色标签调整 */
  .role-tag {
    margin-left: auto;
  }
  
  /* 状态标签调整 */
  .status-badge {
    margin-left: auto;
  }
  
  /* 对话框响应式调整 */
  .el-dialog {
    width: 90% !important;
    max-width: 100%;
  }
  
  .el-form-item__label {
    width: 100% !important;
    text-align: left;
    margin-bottom: 4px;
  }
  
  .el-form-item__content {
    margin-left: 0 !important;
  }
  
  .dialog-footer {
    flex-direction: column;
    gap: 8px;
  }
  
  .dialog-footer .el-button {
    width: 100%;
  }
  
  /* 头部按钮调整 */
  .header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .actions {
    width: 100%;
    display: flex;
    justify-content: space-between;
  }
  
  .actions button {
    padding: 8px 12px;
    flex: 1;
    margin: 0 4px;
  }
}
</style>